import { useRef } from 'react'

import { useHtmlWithPlateTwStylesIsolatedForLastSlide } from '../hooks/useHtmlWithPlateTwStylesIsolated'
import Score from './Score'
import { useTranslation } from '../contexts/localization/Localization'
import useMountEffect from '../hooks/useMountEffect'
import { useAppContext } from '../contexts/AppContext'

function LastSlide({
  hideScore,
  score,
  lastSlideHtml,
}: {
  score: number | undefined
  hideScore: boolean
  lastSlideHtml: string
}) {
  const { text } = useTranslation()
  const { onLastSlideVisible } = useAppContext()
  const ref = useRef<HTMLDivElement>(null)

  // This ref is need to observe whether the last slide is visible,
  // so we can trigger the onLastSlideVisible method
  const visibilityPixelRef = useRef<HTMLDivElement>(null)

  useMountEffect(() => {
    // Handle the last slide visible
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          onLastSlideVisible?.()
        }
      })
    })

    if (visibilityPixelRef.current) {
      observer.observe(visibilityPixelRef.current)
    }

    return () => {
      if (visibilityPixelRef.current) {
        observer.unobserve(visibilityPixelRef.current)
      }
    }
  })

  useHtmlWithPlateTwStylesIsolatedForLastSlide(ref, lastSlideHtml)

  return (
    <div data-role="last-slide" className="shrink-0 h-full overflow-y-auto overflow-x-hidden relative w-full">
      <div
        className={`
              sticky top-0 left-0 right-0 bg-background z-10
              flex w-(--slide-width) items-center justify-between border-b dark:text-white border-solid 
              border-wp-bg-gray text-3xl py-2 px-4
            `}
      >
        <div ref={visibilityPixelRef} className="absolute top-0 left-4 size-px" />
        <div>{text('lastSlideTitle')}</div>
        {!hideScore && !!score && <Score value={score} />}
      </div>

      <div ref={ref} className={`px-8 pb-36`} />
    </div>
  )
}

export default LastSlide
